import React, { useContext, useState, useCallback, useEffect } from 'react';
import { ViewModelServiceContext, ViewModelForm, withViewModelService } from 'gw-portals-viewmodel-react';
import { AdacButtonWithLoader } from 'gw-capability-fnol-adac-react';
import { useTranslator, FormattedMessage } from '@jutro/locale';
import { useDependencies } from 'gw-portals-dependency-react';
import { useValidation } from 'gw-portals-validation-react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import metadata from './AdacLoginPage.metadata.json5';
import messages from './AdacLoginPage.messages';
import AdacCookiesModal from '../../components/AdacCookiesModal/AdacCookiesModal';
import styles from './AdacLoginPage.modules.scss';
import AdacDatePicker from '../../components/AdacDatePicker/AdacDatePicker';
import usePortalModeBasedLabel from '../../hooks/usePortalModeBasedLabel';
import CommunicationVariablesContext from '../../utils/CommunicationVariablesContext';
import { Loader } from '@jutro/components';

const COMMUNICATION_REDIRECT = {
    ebs_signing: {
        mainPagePath: '/ebs',
        historyRedirectPath: '/documents' 
    },
    document_upload: {
        mainPagePath: '/documents',
        historyRedirectPath: '/documents' 
    },
    show_info: {
        mainPagePath: '/service-info',
        historyRedirectPath: '/service-info'
    },
    document_download: {
        mainPagePath: '/my-documents',
        historyRedirectPath: '/my-documents' 
    },
    document_signing: {
        mainPagePath: '/sign-document',
        historyRedirectPath: '/documents' 
    }
};

const ADAC_KFZ_GERMANY = 'vehiclegermany';
const ADAC_KFZ_ABROAD = 'vehicleabroad';
const ADAC_MED = 'medical';

function LoginPage({ match, history }) {
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { token, communicationTypeExt } = match.params;
    const [claimAuthorizationVM, setClaimAuthorizationVM] = useState(viewModelService.create({ communicationTypeExt }, 'cc', 'adac.edge.capabilities.claim.auth.dto.ClaimAuthorizationDTO'));
    const { communicationVariables, setCommunicationVariables } = useContext(CommunicationVariablesContext);
    const { ClaimService } = useDependencies('ClaimService');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState(false);
    const [startDate, setStartDate] = useState(null);

    const onValueChange = useCallback((value, path) => {
        const updatedVM = viewModelService.clone(claimAuthorizationVM);
        _.set(updatedVM.value, path, value);
        setClaimAuthorizationVM(updatedVM);
    }, [viewModelService, claimAuthorizationVM, setClaimAuthorizationVM]);

    const {
        onValidate
    } = useValidation('LoginPage');

    useEffect(async () => {
        setIsValidating(true);
        try {
            await ClaimService.validateToken(token);
        } catch(err) {
            history.push({
                pathname: '/linkexpired'
            });
        } finally {
            setIsValidating(false);
        }
    }, [token]);

    const getCommunicationTypeAndSession = useCallback(async () => {
        setError(false);
        setIsLoading(true);
        try {
            const claimCommunication = await ClaimService.getCommunicationTypeAndSession(
                token,
                claimAuthorizationVM.value
            );

            const redirects = COMMUNICATION_REDIRECT[claimCommunication.communicationType];
            setCommunicationVariables({
                token: token,
                ...claimCommunication,
                historyRedirectPath: redirects.historyRedirectPath
            })

            history.push({
                pathname: redirects.mainPagePath || '/callcenter',
            });
        } catch (err) {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    }, [ClaimService, token, claimAuthorizationVM.value, history]);

    const setStartDateWrapper = useCallback((date) => {
        const chosenDate = date ? {
            year: date.getFullYear(),
            month: date.getMonth(),
            day: date.getDate()
        } : null;
        setStartDate(date);
        onValueChange(chosenDate, 'birthDate');
    }, [onValueChange]);

    const overrides = {
        loginHeader: {
            content: usePortalModeBasedLabel('loginHeader', null, communicationTypeExt)
        },
        registerBtn: {
            onClick: getCommunicationTypeAndSession,
            disabled: !claimAuthorizationVM.aspects.subtreeValid || isLoading,
            isLoading: isLoading,
            label: translator(messages.register)
        },
        termsOfUse: {
            content: <FormattedMessage
                {...messages.termsOfUseText}
                values={{
                    termsOfUse: (
                        <a
                            href={translator(messages.termsOfUseLink)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {`${translator(messages.termsOfUse)}`}
                        </a>
                    )
                }}
            />
        },
        dataProtection: {
            content: <FormattedMessage
                {...messages.dataProtectionText}
                values={{
                    dataProtection: (
                        <a
                            href={translator(messages.dataProtectionLink)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {`${translator(messages.dataProtection)}`}
                        </a>
                    )
                }}
            />
        },
        error: {
            visible: error
        },
        claimNumber: {
            visible: communicationTypeExt === ADAC_MED
        },
        introText: {
            content: usePortalModeBasedLabel('toGoToDetails', null, communicationTypeExt)
        },
        dateOfBirth: {
            visible: communicationTypeExt === ADAC_MED,
            content: (
                <AdacDatePicker
                    date={startDate}
                    isValid={claimAuthorizationVM.birthDate.aspects.subtreeValid}
                    setDate={setStartDateWrapper}
                    label={translator(messages.dateOfBirth)}
                    placeholder={translator(messages.placeholder)}
                />
            )
        },
        licensePlate: {
            placeholder: translator(messages.licensePlatePlaceholder),
            visible: [ADAC_KFZ_GERMANY, ADAC_KFZ_ABROAD].includes(communicationTypeExt)
        },
        membershipNumber: {
            placeholder: translator(messages.membershipNumberPlaceholder),
            visible: [ADAC_KFZ_GERMANY, ADAC_KFZ_ABROAD].includes(communicationTypeExt)
        }
    };

    const resolvers = {
        resolveComponentMap: {
            buttonWitLoader: AdacButtonWithLoader
        }
    };

    return (
        <>
            <AdacCookiesModal />
            <Loader loaded={!isValidating} />
            <ViewModelForm
                uiProps={metadata.pageContent}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
                overrideProps={overrides}
                onValidationChange={onValidate}
                onValueChange={onValueChange}
                model={claimAuthorizationVM}
            />
        </>
    );
}

export default withRouter(withViewModelService(LoginPage));
